import { AlertIconDark, AlertIconLight } from "@images/svgIcons/alertIcon"
import { useUser } from "@modules/auth/hooks/useUser"
import { word } from "@modules/core/utils/i18n"
import { currencySymbol, formatCurrency } from "@modules/currency/currency"
import { FinanceFeesAlertCard } from "@modules/finances/components/financeFeesAlertCard"
import { useLanguage } from "@modules/language/hooks/useLanguage"
import { ProgramBundleType } from "@modules/programBundle/api/programBundleDto"
import { ProgramBundleFormPageProps } from "@modules/programBundle/components/programBundleForm"
import { OnBoardingStripeModal, OnBoardingStripeModalRef } from "@modules/programs/components/onBoardingStripeModal"
import { usePrograms } from "@modules/programs/hooks/usePrograms"
import { FormikInput } from "@modules/ui/components/formikInput"
import { HUButton } from "@modules/ui/components/huButton"
import { HUText } from "@modules/ui/components/huText"
import { ThemeMode } from "@modules/ui/styles/theme"
import { ArrowLeft } from "lucide-react"
import { Dropdown } from "primereact/dropdown"
import { useEffect, useRef, useState } from "react"
import { useTheme } from "styled-components"

export const ProgramBundleFormThirdPage = ({
  formik,
  isFormFieldInValid,
  getFormErrorMessage,
  handleSubmit,
  setPageNumber,
  isSubmitting,
  isEdited,
}: ProgramBundleFormPageProps) => {
  const onBoardingStripeModalRef = useRef<OnBoardingStripeModalRef>(null)

  const theme = useTheme()
  const userProgramsAsync = usePrograms({ programFilter: "OWNED" })
  const filterPrograms = userProgramsAsync.result.filter((program) => formik.values.programIds.includes(program.id))
  const user = useUser()
  const language = useLanguage()
  const sumOfSelectedProgramPrices = filterPrograms.reduce((acc, program) => {
    return acc + (program.price?.value ?? 0)
  }, 0)

  const enum programBundlePice {
    FREE = "FREE",
    PAID = "PAID",
  }
  const dropdownOptions = [
    { label: word("programBundle.form.pricing.free"), value: programBundlePice.FREE },
    { label: word("programBundle.form.pricing.priced"), value: programBundlePice.PAID },
  ]
  const [dropdownValue, setDropdownValue] = useState(
    formik.values.price ? programBundlePice.PAID : programBundlePice.FREE,
  )

  const handleNextStep = () => {
    const priceValue = formik.values.priceInput
    if (dropdownValue !== programBundlePice.FREE) {
      formik.setFieldValue("price", { value: priceValue, currency: user?.stripeAccountCurrency })
    }
    handleSubmit()
  }

  const setupStripeAccount = () => {
    onBoardingStripeModalRef.current?.show()
  }

  useEffect(() => {
    if (!formik.values.addFreeTrial) formik.setFieldValue("trialDuration", null)
  }, [formik.values.addFreeTrial])

  return (
    <div className="flex flex-column gap-3">
      {/* TITLE */}
      <div className="flex flex-column">
        <HUText fontStyle="BS" color={theme.neutralColor600}>
          {word("programBundle.form.thirdPage.step")}
        </HUText>
        <HUText fontStyle="HM" color={theme.neutralColor900} className="mt-4">
          {word("programBundle.form.thirdPage.title")}
        </HUText>
        <HUText fontStyle="BM" color={theme.neutralColor900} className="mt-1">
          {word("programBundle.form.thirdPage.subtitle")}
        </HUText>
      </div>

      {/* PRICING */}
      <div className="flex flex-column mt-4 gap-2 ">
        <HUText type="label" color={theme.inputLabelColor} fontStyle="LS" style={{ lineHeight: "21px" }}>
          {word("programBundle.form.pricing.label").toUpperCase()}
          <HUText fontStyle="LL" color={theme.necessaryInput}>
            {" "}
            *
          </HUText>
        </HUText>
        <Dropdown
          className={"rounded-full"}
          onChange={(e) => {
            setDropdownValue(e.value)
            if (e.value === programBundlePice.FREE) {
              formik.setFieldValue("price", null)
            } else {
              formik.setFieldValue("price", { value: 0, currency: user?.stripeAccountCurrency })
            }
          }}
          value={dropdownValue}
          options={dropdownOptions}
          disabled={user?.stripeAccountStatus !== "OK"}
        />

        {dropdownValue === programBundlePice.PAID && (
          <>
            <FormikInput
              isInvalid={isFormFieldInValid("priceInput")}
              getFieldProps={formik.getFieldProps}
              name="priceInput"
              error={getFormErrorMessage("priceInput")}
              type="number"
              numberProps={{
                currency: user?.stripeAccountCurrency,
                step: 0.1,
                min: 0,
                placeholder: word("offer.form.price.placeholder", {
                  minPrice: formatCurrency(user?.stripeMinimumAmount ?? 0, user?.stripeAccountCurrency, language),
                }),
                onChange: (e) => {
                  formik.setFieldValue("price", { value: e.value, currency: user?.stripeAccountCurrency })
                  formik.setFieldValue("priceInput", e.value)
                },
                className: "flex-grow-1",
              }}
              setFieldValue={formik.setFieldValue}
              formikStyle={{ flex: 1, maxWidth: "100%", marginBottom: 0 }}
              rightButton={
                formik.values.type === ProgramBundleType.VIRTUAL ? (
                  <HUText fontStyle="TM" className="ml-1">
                    {`${currencySymbol(user?.stripeAccountCurrency, language)} / ${word("global.month.label", { count: 1 })}`}
                  </HUText>
                ) : (
                  <HUText fontStyle="TM" className="ml-1">
                    {`${currencySymbol(user?.stripeAccountCurrency, language)}`}
                  </HUText>
                )
              }
            />
            <FinanceFeesAlertCard />
          </>
        )}
      </div>

      <div className="flex flex-column align-items-center gap-2 card">
        <HUText fontStyle="BS" color={theme.neutralColor700}>
          {word("programBundle.form.pricing.sumOfSelectedProgramPrices")}
        </HUText>
        <HUText fontStyle="LM" color={theme.mainBlack}>
          {sumOfSelectedProgramPrices > 0
            ? formatCurrency(sumOfSelectedProgramPrices, user?.stripeAccountCurrency, language)
            : word("programBundle.form.pricing.free")}
        </HUText>
      </div>

      {/* TRIAL */}
      {dropdownValue !== programBundlePice.FREE && formik.values.type === ProgramBundleType.VIRTUAL && (
        <div className="flex flex-column gap-2">
          <HUText
            type="label"
            color={theme.neutralColor700}
            fontStyle="LS"
            style={{ lineHeight: "21px", marginBottom: 0 }}
          >
            {word("program.price.trial.label").toUpperCase()}
          </HUText>
          <div className="card flex flex-column align-items-stretch mt-0 w-full flex-grow-1 p-0 pb-3">
            <FormikInput
              type="switchInline"
              textSwitch={word("program.price.trial.inputLabel")}
              name="addFreeTrial"
              getFieldProps={formik.getFieldProps}
              isInvalid={isFormFieldInValid("addFreeTrial")}
              error={getFormErrorMessage("addFreeTrial")}
              setFieldValue={formik.setFieldValue}
              switchProps={{
                className: "mt-2 px-2 mb-0",
                checked: formik.values.addFreeTrial,
              }}
            />

            <div className="mx-4">
              <FormikInput
                type="number"
                name="trialDuration"
                getFieldProps={formik.getFieldProps}
                isInvalid={isFormFieldInValid("trialDuration")}
                error={getFormErrorMessage("trialDuration")}
                setFieldValue={formik.setFieldValue}
                disabled={!formik.values.addFreeTrial}
                numberProps={{
                  step: 1,
                  min: 0,
                  max: 365,
                  className: "flex-grow-1",
                  maxFractionDigits: 0,
                }}
                rightButton={
                  <HUText fontStyle="TM" className="ml-1">
                    {word("global.day.label", { count: 2 })}
                  </HUText>
                }
              />
            </div>
          </div>
        </div>
      )}

      {/* STRIPE ACCOUNT SET UP */}
      {user?.stripeAccountStatus !== "OK" && (
        <div className="flex flex-column align-items-center gap-2 mt-4 card">
          {theme.mode === ThemeMode.ATHLETE ? (
            <AlertIconLight color={theme.error} />
          ) : (
            <AlertIconDark color={theme.error} />
          )}

          <HUText fontStyle="LL" className="text-center">
            {word("programBundle.form.stripeAccount.error.title")}
          </HUText>
          <HUText
            fontStyle="BS"
            className="text-center"
            style={{ whiteSpace: "pre-line", color: theme.neutralColor600 }}
          >
            {word("programBundle.form.stripeAccount.error.description")}
          </HUText>

          <HUButton
            text={word("programBundle.form.stripeAccount.error.buttonLabel")}
            type="Right_Icon"
            size="M"
            colorType="Primary"
            className="mt-2"
            onClick={setupStripeAccount}
          />
        </div>
      )}

      {/* NEXT BUTTON */}
      <div className="flex gap-5 mt-5 mb-6">
        <HUButton
          text={word("global.previous")}
          onClick={() => setPageNumber((prev) => prev - 1)}
          type="Rounded"
          size="M"
          colorType="Quaternary"
          fullWidth
          icon={{
            iconView: <ArrowLeft size={18} color={theme.mainBlack} />,
          }}
        />
        <HUButton
          disabled={isSubmitting || isFormFieldInValid("priceInput") || isFormFieldInValid("trialDuration")}
          text={isEdited ? word("global.edit") : word("global.create")}
          onClick={handleNextStep}
          type="Right_Icon"
          size="M"
          colorType="Primary"
          fullWidth
        />
      </div>

      <OnBoardingStripeModal ref={onBoardingStripeModalRef} />
    </div>
  )
}
